/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, useCallback, useEffect, useMemo, useRef, useState, useTransition, } from 'react';
import {
    type ShouldRevalidateFunctionArgs,
    useAsyncError,
    useLocation,
    useNavigation,
    useRouteLoaderData,
} from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import type { loader as rootLoader } from '@/root';
import type { Route } from './+types/_app.category.$categoryId';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import CategoryPagination from '@/components/category-pagination';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
import CategoryRefinements from '@/components/category-refinements';
import CategorySorting from '@/components/category-sorting';
import DeferredProductGrid from '@/components/product-grid';
import QuickFilters from '@/components/quick-filters';
import { ProductCarouselWithData } from '@/components/product-carousel/carousel';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region } from '@/components/region';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import CategoryBanner from '@/components/category-banner';
import CategoryBannerSkeleton from '@/components/category-banner/skeleton';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { generateCategorySchema } from '@/utils/category-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import {
    getInitialFiltersOpen,
    getSearchWithoutClientOnlyParams,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { getLogger } from '@/lib/logger.server';
import { ArrowUpDown, X } from 'lucide-react';

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'plpTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of the results',
        maxComponents: 5,
    },
    {
        id: 'plpTopContent',
        name: 'Top Content Region',
        description: 'Content width region below sort/filter, above product grid',
        maxComponents: 5,
    },
    {
        id: 'plpBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class ProductListingPageMetadata { }

type CategoryPageData = {
    category: ShopperProducts.schemas['Category'];
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    limit: number;
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    page: ReturnType<typeof fetchPageWithComponentData>;
    categoryId: string;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    categorySchema: Promise<ReturnType<typeof generateCategorySchema> | null>;
    wishlistInitialState: Promise<WishlistInitialState>;
};

/**
 * Server-side loader function that fetches category data and product search results.
 * This function runs on the server during SSR and prepares data for the category page.
 * @returns Object containing search results, category data, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<CategoryPageData> {
    const {
        context,
        request,
        params: { categoryId },
    } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const logger = getLogger(context);
    logger.debug('Category: loader starting', {
        categoryId,
        offset: parseInt(searchParams.get('offset') || '0', 10),
    });
    const offset = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || '0', 10);
    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);

    // Get currency and locale for cache-busting the page key
    const config = getConfig(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;
    const limit = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.LIMIT ?? 'limit') || '', 10) || config.search.products.hits.limit;

    let categoryData: ShopperProducts.schemas['Category'] | undefined;
    try {
        categoryData = await fetchCategory(context, categoryId, 2);
        console.log('Category inside Loader:', categoryData);
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }


    const nonCgidRefine = refine.filter((r) => !r.startsWith('cgid='));
    const cgidsToFetch = refine.filter((r) => r.startsWith('cgid=')).map((r) => r.slice(5));
    if (!cgidsToFetch.length) cgidsToFetch.push(categoryId);

    const allCgidResults = await Promise.all(
        cgidsToFetch.map((cgid) =>
            fetchSearchProducts(context, { limit, offset, sort, refine: [...nonCgidRefine, `cgid=${cgid}`], currency }).catch(() => null)
        )
    );
    const mergedHits = [...new Map(allCgidResults.flatMap((r) => r?.hits ?? []).map((h) => [h.productId, h])).values()];
    const searchResultCritical = { ...(allCgidResults[0] ?? {}), hits: mergedHits.slice(0, limit), total: mergedHits.length } as any;

    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalLimit = Math.max(0, limit - effectiveCriticalCount);
    const searchResultNonCritical = nonCriticalLimit > 0
        ? fetchSearchProducts(context, {
            limit: nonCriticalLimit,
            offset: offset + effectiveCriticalCount,
            sort,
            refine: [...nonCgidRefine, `cgid=${cgidsToFetch[0]}`],
            currency,
        })
        : Promise.resolve({ total: searchResultCritical.total, hits: [], offset: offset + effectiveCriticalCount, limit: 0 });

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // Generate category schema in loader (server-side) for SEO
    const categorySchemaPromise = searchResultNonCritical
        .then((searchResult: ShopperSearch.schemas['ProductSearchResult']) => {
            try {
                // Use public origin from request headers instead of request.url
                // to avoid exposing internal AWS Lambda URLs in schema
                const publicOrigin = getPublicOrigin(request);
                const url = new URL(request.url);
                const schemaPageUrl = `${publicOrigin}${url.pathname}${url.search}`;
                // Validate inputs before generating schema
                if (!categoryData || !searchResult) {
                    return null;
                }
                return generateCategorySchema({
                    category: categoryData,
                    searchResult: {
                        ...searchResult,
                        hits: [...(searchResultCritical.hits || []), ...(searchResult.hits || [])],
                    },
                    config,
                    pageUrl: schemaPageUrl,
                    defaultCurrency: currency,
                });
            } catch (error) {
                logger.error('Error generating category schema in loader', {
                    error,
                });
                return null;
            }
        })
        .catch((error) => {
            logger.error('Error in category schema promise chain', {
                error,
            });
            return null;
        });

    return {
        category: categoryData,
        searchResultCritical,
        limit,
        searchResultNonCritical,
        page: fetchPageWithComponentData(args, {
            aspectType: 'plp',
            categoryId,
        }),
        categoryId,
        pageUrl,
        refine: [...nonCgidRefine, ...cgidsToFetch.map((id) => `cgid=${id}`)],
        currency,
        locale,
        initialFiltersOpen,
        categorySchema: categorySchemaPromise,
        wishlistInitialState: fetchWishlistInitialState(args.context),
    };
}

export function shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) {
    const clientOnlyParamsChanged =
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search !== nextUrl.search &&
        getSearchWithoutClientOnlyParams(currentUrl.search) === getSearchWithoutClientOnlyParams(nextUrl.search);

    if (clientOnlyParamsChanged) {
        return false;
    }

    return defaultShouldRevalidate;
}

/**
 * Category page component that displays a product category with filtering, sorting, and pagination.
 * This component uses the createPage factory to handle Suspense patterns.
 * @returns JSX element representing the category page
 */
function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

/**
 * Component that renders JSON-LD schema when categorySchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function CategoryJsonLd({
    categorySchemaPromise,
}: {
    categorySchemaPromise: Promise<ReturnType<typeof generateCategorySchema> | null>;
}) {
    const categorySchema = use(categorySchemaPromise);
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;
    return categorySchema ? <JsonLd data={categorySchema} id="category-schema" nonce={nonce} /> : null;
}

export default function CategoryPage({
    loaderData: {
        category,
        searchResultCritical,
        limit,
        searchResultNonCritical,
        page,
        categoryId,
        pageUrl,
        refine,
        locale,
        currency,
        initialFiltersOpen,
        categorySchema,
        wishlistInitialState,
    },
}: {
    loaderData: CategoryPageData;
}) {
    const config = useConfig();

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);
    const [mobileSortOpen, setMobileSortOpen] = useState(false);
    const [draftSort, setDraftSort] = useState<string | undefined>(undefined);
    // const limit = config.search.products.hits.limit;
    const navigate = useNavigate();

    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(
        0,
        Math.min(limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedDataRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${categoryId}-${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
        const shouldLockScroll = isMobileViewport && (filtersOpen || mobileSortOpen);
        document.body.style.overflow = shouldLockScroll ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [filtersOpen, mobileSortOpen]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
        if (isMobileViewport && filtersOpen) {
            toggleFiltersOpen();
        }
    }, []);

    useEffect(() => {
        // Only track if we haven't already tracked this specific data combination
        if (analyticsKey !== lastTrackedDataRef.current) {
            lastTrackedDataRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        if (analytics) {
                            void analytics.trackViewCategory({
                                category,
                                searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                                sort:
                                    searchResultCritical.selectedSortingOption ||
                                    searchResultCritical.sortingOptions?.[0]?.label ||
                                    '',
                                refinements: searchResultCritical.selectedRefinements ?? {},
                            });
                        }
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, category, analyticsKey, nonCriticalPromise]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInCategory({
                    category,
                    product,
                });
            }
        },
        [analytics, category]
    );

    const applySortAndClose = useCallback(() => {
        if (draftSort) {
            const params = new URLSearchParams(location.search);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, draftSort);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
            void navigate({ ...location, search: `?${params.toString()}` });
        }
        setMobileSortOpen(false);
    }, [draftSort, location, navigate]);

    const resetSortAndClose = useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete(PRODUCT_SEARCH_QUERY_PARAMS.SORT);
        params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
        void navigate({ ...location, search: `?${params.toString()}` });
        setMobileSortOpen(false);
    }, [location, navigate]);

    return (
        <WishlistProvider initialState={wishlistInitialState}>
            <SeoMeta
                title={category.name || category.id}
                description={category.pageDescription || category.description}
                openGraph={{
                    type: 'website',
                    url: pageUrl,
                }}
            />
            <div className="plp-page ">
                {/* plpTopFullWidth — full-width banner region, flush to the header (mirrors homepage pattern) */}
                {/* <Region
                    page={page}
                    regionId="plpTopFullWidth"
                    fallbackElement={<CategoryBannerSkeleton />}
                    errorElement={<CategoryBanner />}
                    fallbackOnEmpty
                /> */}

                <div className="section-container max-w-none">
                    <div className="mb-4">
                        <CategoryBreadcrumbs category={category} />
                    </div>

                    <div className="plp-header">
                        <div className="plp-header-info">
                            <h1 className="plp-header-title text-[16px] md:text-[20px] uppercase">
                                {category?.name || category.id} ({searchResultCritical.total})
                            </h1>
                            <span className="plp-header-subtitle text-[12px] md:text-[16px] text-[#35353B]">
                                Explore our exciting new Mahindra merchandise! From bold statement items to your daily must-haves, there&apos;s something for everyone.
                            </span>
                        </div>
                        <UITarget targetId="sfcc.plp.search.summary" />
                    </div>

                    <div className="plp-content-layout" style={{ display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'flex-start' }}>

                        {/* Category Refinements - desktop sidebar */}
                        <div className="plp-sidebar hidden md:block w-[23%] shrink-0 rounded-[10px] bg-[#F5F5F5] mb-5">
                            <CategoryRefinements
                                result={searchResultCritical}
                                refine={refine}
                                category={category}
                                onToggle={toggleFiltersOpen}
                            />
                        </div>

                        <div className="plp-main-content" style={{ width: "100%" }}>
                            <ActiveFilters result={searchResultCritical} />

                            {/* plpTopContent */}
                            <Region className="mb-8" page={page} regionId="plpTopContent" />

                            <UITarget targetId="sfcc.plp.agent.categoryHelper" />
                            <UITarget targetId="sfcc.plp.search.results">
                                {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                                    <div className='hidden md:block'>
                                        <div className="plp-main-sorting mb-4 justify-end">
                                            <CategorySorting result={searchResultCritical} />
                                        </div>
                                    </div>
                                )}
                                <div className="plp-product-grid">
                                    <DeferredProductGrid
                                        key={productGridDataKey}
                                        critical={searchResultCritical.hits ?? []}
                                        nonCritical={nonCriticalPromise}
                                        nonCriticalCount={nonCriticalCount}
                                        hasRefinementsPanel={true}
                                        isLoading={isProductGridLoading}
                                        handleProductClick={handleProductClick}
                                        topCategoryName={
                                            category.parentCategoryTree?.find((p) => p.id !== 'root')?.name ?? category.name
                                        }
                                        tileClassName="!border !border-[#E3E5E9]   !rounded-[12px]   "
                                        errorElement={<ProductGridError />}
                                    />
                                </div>
                            </UITarget>

                            {searchResultCritical.total > 0 && (
                                <div className="mt-10">
                                    <CategoryPagination
                                        limit={limit}
                                        offset={searchResultCritical.offset}
                                        total={searchResultCritical.total}
                                        limitOptions={[4, 8]}
                                    />
                                </div>
                            )}

                            {/* Carousel of category products */}


                            {/* plpBottom */}
                            <Region className="mt-8" page={page} regionId="plpBottom" />
                        </div>

                    </div>
                    {searchResultCritical.hits && searchResultCritical.hits.length > 0 && (
                        <div className="md:mt-16">
                            <ProductCarouselWithData
                                data={searchResultCritical.hits}
                                title="RECOMMENDED PRODUCTS"
                                titleClassName="text-[20px] md:text-[32px]"
                                alwaysShowControls={true}
                            />
                        </div>
                    )}
                </div>
                <div className="fixed bottom-0 left-0 right-0 z-30 flex h-[52px] items-center border-t border-[#E5E5E5] bg-white md:hidden">
                    <button
                        type="button"
                        onClick={() => setMobileSortOpen(true)}
                        className="flex h-full flex-1 items-center justify-center gap-2 text-sm font-normal text-[#131315]">
                        <ArrowUpDown className="size-4 shrink-0 text-[#131315]" strokeWidth={1.5} />
                        <span>Sort By</span>
                    </button>
                    <div
                        className="mobile-sort-filter-divider"
                        style={{ width: '1px', height: '26px', backgroundColor: '#131315' }}
                        aria-hidden="true"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setMobileSortOpen(false);
                            if (!filtersOpen) {
                                toggleFiltersOpen();
                            }
                        }}
                        className="flex h-full flex-1 items-center justify-center gap-2 text-sm font-normal text-[#131315]">
                        <svg
                            width="17"
                            height="9"
                            viewBox="0 0 17 9"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="shrink-0">
                            <path
                                d="M13.75 4.0625C13.75 4.14538 13.7171 4.22487 13.6585 4.28347C13.5999 4.34208 13.5204 4.375 13.4375 4.375H3.4375C3.35462 4.375 3.27513 4.34208 3.21653 4.28347C3.15792 4.22487 3.125 4.14538 3.125 4.0625C3.125 3.97962 3.15792 3.90013 3.21653 3.84153C3.27513 3.78292 3.35462 3.75 3.4375 3.75H13.4375C13.5204 3.75 13.5999 3.78292 13.6585 3.84153C13.7171 3.90013 13.75 3.97962 13.75 4.0625ZM16.5625 0H0.3125C0.22962 0 0.150134 0.0329238 0.0915292 0.0915289C0.0329241 0.150134 0 0.22962 0 0.3125C0 0.39538 0.0329241 0.474866 0.0915292 0.533471C0.150134 0.592076 0.22962 0.625 0.3125 0.625H16.5625C16.6454 0.625 16.7249 0.592076 16.7835 0.533471C16.8421 0.474866 16.875 0.39538 16.875 0.3125C16.875 0.22962 16.8421 0.150134 16.7835 0.0915289C16.7249 0.0329238 16.6454 0 16.5625 0ZM10.3125 7.5H6.5625C6.47962 7.5 6.40013 7.53292 6.34153 7.59153C6.28292 7.65013 6.25 7.72962 6.25 7.8125C6.25 7.89538 6.28292 7.97487 6.34153 8.03347C6.40013 8.09208 6.47962 8.125 6.5625 8.125H10.3125C10.3954 8.125 10.4749 8.09208 10.5335 8.03347C10.5921 7.97487 10.625 7.89538 10.625 7.8125C10.625 7.72962 10.5921 7.65013 10.5335 7.59153C10.4749 7.53292 10.3954 7.5 10.3125 7.5Z"
                                fill="#131315"
                            />
                        </svg>
                        <span>Filter</span>
                        {selectedFiltersCount > 0 && (
                            <span className="flex size-5 items-center justify-center rounded-full bg-black text-[10px] text-white">
                                {selectedFiltersCount}
                            </span>
                        )}
                    </button>
                </div>

                {filtersOpen && (
                    <div className="md:hidden">
                        <button type="button" aria-label="Close filters" className="fixed inset-0 z-[9998] bg-black/50" onClick={toggleFiltersOpen} />
                        <div className="fixed bottom-0 left-0 right-0 z-[9999] h-auto overflow-hidden rounded-t-[16px] bg-white shadow-2xl">
                            <CategoryRefinements result={searchResultCritical} refine={refine} category={category} onToggle={toggleFiltersOpen} />
                        </div>
                    </div>
                )}

                {mobileSortOpen && (
                    <div className="md:hidden">
                        <button type="button" aria-label="Close sort" className="fixed inset-0 z-[9998] bg-black/50" onClick={() => setMobileSortOpen(false)} />
                        <div className="fixed bottom-0 left-0 right-0 z-[9999] rounded-t-[16px] bg-white p-4 shadow-2xl">
                            <div className="mb-2 flex items-center justify-between pb-4">
                                <span className="text-[20px] font-semibold">
                                    Sort By
                                </span>
                                <button type="button" onClick={() => setMobileSortOpen(false)} className="flex size-8 items-center justify-center" aria-label="Close sort">
                                    <X className="size-8" strokeWidth={1} />
                                </button>
                            </div>
                            <CategorySorting result={searchResultCritical} displayAs="list" draftValue={draftSort} onDraftChange={setDraftSort} />
                            <div className="mt-[24px] flex gap-3">
                                <button type="button" onClick={resetSortAndClose} className="h-11 flex-1 rounded-full border border-black bg-white text-sm font-medium">
                                    Reset
                                </button>
                                <button type="button" onClick={applySortAndClose} className="h-11 flex-1 rounded-full bg-black text-sm font-medium text-white">
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Suspense fallback={null}>
                <CategoryJsonLd categorySchemaPromise={categorySchema} />
            </Suspense>
        </WishlistProvider>
    );
}
